import { css } from '@emotion/react';
import isPropValid from '@emotion/is-prop-valid';
import styled from '@emotion/styled';
import Link from 'next/link';
import React from 'react';
import { colors, spacing } from '../../../utils/styleguide';
import Icon, { IconNames } from '../Icon';
import Typography from '../text/Typography';

interface GhostProps {
  underlineColor: string;
  hideUnderline?: boolean;
}

const StyledTypography = styled(Typography, {
  shouldForwardProp: (prop) => {
    return isPropValid(prop) || prop === 'underlineColor' || prop === 'tag' || prop === 'variant';
  },
})(
  (props: GhostProps) => `
  text-decoration: none;
  background-color: transparent;
  border: none;
  padding: 0;
  border-bottom: ${props.hideUnderline ? 0 : '2px'} solid ${props.underlineColor};
  cursor: pointer;
`,
);

const Container = styled.div`
  display: inline-flex;
  align-items: center;
`;

type Props = {
  onClick?: () => void;
  href?: string;
  label?: string;
  color?: string;
  underlineColor?: string;
  className?: string;
  hideUnderline?: boolean;
  leadingIconName?: IconNames;
  trailingIconName?: IconNames;
  textVariant?: 'buttonSmall' | 'smallTextBold';
};

const GhostButton = ({
  onClick,
  href = '',
  label,
  color = colors.darkGrey,
  underlineColor = colors.darkGrey,
  className,
  hideUnderline,
  leadingIconName,
  trailingIconName,
  textVariant = 'buttonSmall',
}: Props) => {
  const handleOnClick = (event: React.MouseEvent) => {
    event.preventDefault();
    if (onClick) {
      onClick();
    }
  };
  return (
    <Link href={href} passHref={!!href}>
      <Container>
        {leadingIconName && (
          <Icon
            name={leadingIconName}
            stroke={color}
            css={css`
              margin-right: ${spacing[0]}px;
            `}
          />
        )}
        <StyledTypography
          useBuiltInMargins
          className={className}
          color={color}
          variant={textVariant}
          tag={href ? 'a' : 'button'}
          href={href}
          onClick={onClick ? handleOnClick : () => null}
          underlineColor={underlineColor}
          hideUnderline={hideUnderline}
        >
          {label}
        </StyledTypography>
        {trailingIconName && (
          <Icon
            onClick={onClick ? handleOnClick : () => null}
            name={trailingIconName}
            stroke={color}
            css={css`
              margin-left: ${spacing[0]}px;
            `}
          />
        )}
      </Container>
    </Link>
  );
};

export default GhostButton;
